import { useState } from "react";
import { useForm } from "react-hook-form";

import Input from "../FormElements/Input";
import Button from "../../atomic/Button";
import { LoanApplicationService } from "../../../services/LoanApplication";

interface EmailEditOTPModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultValue: string;
  loanApplicationID: string;
  token: string;
  pageName: string;
  notify: any;
  onSuccess?: () => void;
  isLenderPlatform: boolean;
  selectedCoApplicantUserID: string;
}

interface FormData {
  email: string;
  otp: string;
}

export default function EmailEditOTPModal({
  isOpen,
  onClose,
  defaultValue,
  loanApplicationID,
  token,
  pageName,
  notify,
  isLenderPlatform,
  onSuccess,
  selectedCoApplicantUserID,
}: EmailEditOTPModalProps) {
  const [showOtpField, setShowOtpField] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isOtpLoading, setIsOtpLoading] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid },
    reset,
  } = useForm<FormData>({
    defaultValues: {
      email: defaultValue,
      otp: "",
    },
    mode: "onChange",
  });

  const emailValue = watch("email");
  const otpValue = watch("otp");

  // Email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const handleSendOtp = async () => {
    if (!emailValue || !emailRegex.test(emailValue)) {
      notify({
        title: "Error",
        text: "Please enter a valid email address",
        type: "error",
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await LoanApplicationService.sendEmailOtp({
        token,
        pageName,
        isLenderPlatform,
        payload: {
          userId: selectedCoApplicantUserID,
          loanApplicationId: loanApplicationID,
          otpType: "mfl_email_verification",
          email: emailValue,
        },
      });

      if (response?.status) {
        setShowOtpField(true);

        notify({
          title: "Success",
          text: "OTP sent successfully to your email",
          type: "success",
        });
      } else {
        notify({
          title: "Error",
          text:
            (response?.data as any)?.msg ||
            response?.error ||
            "Failed to send OTP",
          type: "error",
        });
      }
    } catch (error) {
      notify({
        title: "Error",
        text: "Failed to send OTP. Please try again.",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (data: FormData) => {
    if (!data.otp || data.otp.length < 4) {
      notify({
        title: "Error",
        text: "Please enter a valid OTP",
        type: "error",
      });
      return;
    }

    setIsOtpLoading(true);
    try {
      const response = await LoanApplicationService.verifyEmailOtp({
        token,
        pageName,
        isLenderPlatform,
        payload: {
          userId: selectedCoApplicantUserID,
          loanApplicationId: loanApplicationID,
          otpType: "mfl_email_verification",
          otp: Number(data.otp),
        },
      });

      if (response?.status) {

        notify({
          title: "Success",
          text: "Email verified successfully",
          type: "success",
        });
        onSuccess?.();
        handleClose();
      } else {
        notify({
          title: "Error",
          text:
            (response?.data as any)?.msg ||
            response?.error ||
            "Failed to verify OTP",
          type: "error",
        });
      }
    } catch (error) {
      notify({
        title: "Error",
        text: "Failed to verify OTP. Please try again.",
        type: "error",
      });
    } finally {
      setIsOtpLoading(false);
    }
  };

  const handleClose = () => {
    setShowOtpField(false);
    reset();
    onClose();
  };

  const isEmailValid = emailValue && emailRegex.test(emailValue);
  const isOtpValid = otpValue && otpValue.length >= 4;

  if (!isOpen) return null;

  return (
    <form
      onSubmit={handleSubmit(handleVerifyOtp)}
      className="space-y-4 p-4" //bg-white rounded-lg p-6 w-full max-w-md mx-4
    >
      <Input
        type="email"
        label="Email Address"
        requiredIcon={true}
        register={register("email", {
          required: "Email is required",
          pattern: {
            value: emailRegex,
            message: "Please enter a valid email address",
          },
          validate: (value) => {
            if (!value || value.trim().length === 0) {
              return "Email is required";
            }
            return true;
          },
        })}
        error={errors.email}
        errorText={errors.email?.message}
      />

      <div className="flex justify-end">
        <Button
          type="button"
          onClick={handleSendOtp}
          disabled={!isEmailValid || isLoading}
          isLoading={isLoading}
          color="primary"
        >
          Send OTP
        </Button>
      </div>

      {showOtpField && (
        <div className="space-y-4">
          <div>
            <Input
              type="text"
              label="OTP"
              requiredIcon={true}
              placeholder="Enter 6-digit OTP"
              register={register("otp", {
                required: "OTP is required",
                minLength: {
                  value: 6,
                  message: "OTP must be at least 6 digits",
                },
                pattern: {
                  value: /^\d+$/,
                  message: "OTP must contain only numbers",
                },
                validate: (value) => {
                  if (!value || value.trim().length === 0)
                    return "OTP is required";

                  return true;
                },
              })}
              error={errors.otp}
              errorText={errors.otp?.message}
            />
          </div>

          <div className="flex justify-end space-x-2">
            <Button
              type="button"
              onClick={handleClose}
              color="default"
              outline
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!isOtpValid || isOtpLoading}
              isLoading={isOtpLoading}
              color="primary"
            >
              Verify OTP
            </Button>
          </div>
        </div>
      )}
    </form>
  );
}
